import type {
  BsExamAnalyticsDetector,
  BsExamAnalyticsPayload,
  BsExamAnalyticsStage,
} from '../../../helpers/api/blind-sweep/bs-analytics';

export type StageKey = 'autoselect' | 'rules_enqueue' | 'rules_execution';

export const STAGE_LABELS: Record<StageKey, string> = {
  autoselect: 'Report autoselect',
  rules_enqueue: 'Rules enqueue',
  rules_execution: 'Rules execution',
};

export function getStageStatus(
  payload: BsExamAnalyticsPayload | undefined,
  stage: StageKey
): string {
  const status = payload?.stages?.[stage]?.status;
  if (status) {
    return status;
  }
  return 'pending';
}

export function isRulesExecutionTerminal(payload?: BsExamAnalyticsPayload): boolean {
  const status = payload?.stages?.rules_execution?.status;
  return status === 'completed' || status === 'failed';
}

export function formatDetectorOutcome(detector: BsExamAnalyticsDetector): string {
  const result = detector.result || {};
  switch (detector.name) {
    case 'presentation':
      return `presentation: ${result.presentation ?? '?'} | facing: ${result.facing ?? '?'}`;
    case 'placenta':
      return `location: ${result.location ?? '?'}`;
    case 'multiple_pregnancy':
      return `type: ${result.pregnancy_type ?? '?'}`;
    case 'cardiac_activity':
      return `detected: ${String(result.detected ?? false)} (${result.frame_count ?? 0} frames)`;
    case 'genitalia':
      return `seen: ${String(result.seen ?? false)}`;
    case 'placenta_previa':
      return `classification: ${result.classification ?? '?'}`;
    default:
      return JSON.stringify(result);
  }
}

export function isWeakDetectorResult(detector: BsExamAnalyticsDetector): boolean {
  const result = detector.result || {};
  const method = String(result.method ?? '').toLowerCase();
  if (detector.name === 'presentation') {
    return result.presentation === 'undetermined' || !result.presentation;
  }
  if (detector.name === 'placenta') {
    return result.location === 'undetermined' || !result.location;
  }
  if (detector.name === 'cardiac_activity') {
    return result.detected === false;
  }
  if (detector.name === 'genitalia') {
    return result.seen === false;
  }
  return method.includes('undetermined') || method.includes('no ');
}

export function stageChipColor(status: string): string {
  switch (status) {
    case 'completed':
    case 'autofilled':
    case 'already_filled':
      return 'green';
    case 'queued':
      return 'blue';
    case 'failed':
      return 'red';
    case 'skipped':
      return 'default';
    default:
      return 'default';
  }
}

export function formatStage(stage?: BsExamAnalyticsStage): string {
  if (!stage?.at) {
    return '';
  }
  try {
    return new Date(stage.at).toLocaleString();
  } catch {
    return stage.at;
  }
}
